/*
 * Coq Magic — React Frontend
 *
 * Structure:
 * - Routes & layout: this file (App.js)
 * - Styles: App.css (cacheable, no runtime injection)
 * - Data: src/data/menuData.js (SAMPLE_MENU), src/data/constants.js (TITLE_MAP, MENU_CATEGORIES)
 * - Heavy pages (Checkout, Admin, Login, SignUp, Account) are lazy-loaded for faster initial load
 *
 * Performance: font preconnect in index.html, video preload="metadata", code-split routes.
 */

import React, { useEffect, useMemo, useRef, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { OrderProvider, useOrder } from './OrderContext';
import { AuthProvider, useAuth } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import PasswordGate from './PasswordGate';
import { submitContactWithRecaptcha, isSupabaseConfigured } from './supabaseClient';
import { SAMPLE_MENU } from './data/menuData';
import { TITLE_MAP, MENU_CATEGORIES } from './data/constants';
import './App.css';

const IS_DEV = process.env.NODE_ENV !== 'production';

const CheckoutPage = lazy(() => import('./CheckoutPage'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const LoginPage = lazy(() => import('./LoginPage'));
const SignUpPage = lazy(() => import('./SignUpPage'));
const AccountPage = lazy(() => import('./AccountPage'));
const PrivacyPage = lazy(() => import('./PrivacyPage'));
const TermsPage = lazy(() => import('./TermsPage'));




function IconSearch() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
}

function IconFilter() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
}

function IconEmail() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
}

function IconPhone() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
}

// IconLocation removed - not currently used in the UI

function IconFacebook() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
}

function IconInstagram() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" /></svg>
}

// IconTwitter and IconLinkedIn removed - not currently used in the UI

function IconCart() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
}


function IconFire() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-2.335-3-3-3a5.5 5.5 0 0 1 5.5-5.5C17.765 0.5 22 4.735 22 9.965 22 17.707 15.965 24 8.5 24 8 24 4 21 4 15.5c0-4.5 3-7 3-7 0 1.5.5 3.5 1.5 6z"></path></svg>
}


function IconCartLarge() {
  return <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
}

function IconPlate() {
  return <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M22 12A10 10 0 1 1 12 2a10 10 0 0 1 10 10Z" /><path d="M12 5a7 7 0 1 1 0 14 7 7 0 0 1 0-14Z" /></svg>
}

function IconCheck() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
}

function IconClose() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
}


function Modal({ isOpen, title, message, onClose, actions }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>{title}</h2>
        <p>{message}</p>
        <div className="modal-actions">
          {actions && actions.map((action, idx) => (
            <button key={idx} className={action.type === 'primary' ? 'btn primary' : 'btn'} onClick={action.onClick}>
              {action.label}
            </button>
          ))}
          {(!actions || actions.length === 0) && (
            <button className="btn primary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  )
}

function Footer() {
  const { pathname } = useLocation();
  const isCheckoutLike = pathname === '/cart' || pathname === '/checkout';
  const [showBanner, setShowBanner] = useState(!isCheckoutLike);

  useEffect(() => {
    if (!isCheckoutLike) {
      setShowBanner(true);
      return;
    }

    setShowBanner(false);

    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const doc = document.documentElement;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const scrollable = doc.scrollHeight - window.innerHeight;
        if (scrollable <= 120) {
          return;
        }

        const bottomDistance = doc.scrollHeight - (scrollY + window.innerHeight);
        if (scrollY > 40 && bottomDistance < 220) {
          setShowBanner(true);
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isCheckoutLike]);

  return (
    <>
      {showBanner && (
        <div className="footer-banner">
          <video style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1, filter: 'blur(3px)' }} autoPlay muted loop playsInline preload="metadata">
            <source src="/assets/videos/AQO7Vjy0kaFcaQxG5tCiBoW2lfOVWgqo2_FJ_wya_6dypjigQotj5eD7ECZBHVGdLhrEfwKMmZDPfzlUzRkvg-ARhRlKvDsdslOf6UY.mp4" type="video/mp4" />
          </video>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(135deg,rgba(10,10,10,0.4),rgba(15,15,15,0.7))', zIndex: 2 }} />
          <div className="container" style={{ position: 'relative', zIndex: 3, textAlign: 'center', color: 'var(--text)' }}>
            <div style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '42px', fontWeight: 900, marginBottom: 16, fontFamily: "'Playfair Display', serif" }}>Découvrez Notre Art Culinaire</h3>
              <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: 32, maxWidth: '600px', marginInline: 'auto' }}>Grillades authentiques au feu de bois, préparées avec passion pour une expérience inoubliable.</p>
              <Link to="/menu" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="btn primary">Voir le Menu Complet</Link>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Coq Magic</h4>
            <p>Découvrez l'excellence culinaire où la tradition rencontre l'innovation. Expérience gastronomique authentique au feu de bois.</p>
            <div className="social-links">
              <a href="https://facebook.com/coqmagicrestaurant" target="_blank" rel="noopener noreferrer" title="Facebook">
                <IconFacebook />
              </a>
              <a href="https://www.instagram.com/coqmagic/" target="_blank" rel="noopener noreferrer" title="Instagram">
                <IconInstagram />
              </a>
              <a href="mailto:restocoq.nk@gmail.com" title="Email">
                <IconEmail />
              </a>
            </div>
          </div>

          <div className="footer-section">
            <h4>Suivez-nous</h4>
            <div className="footer-links">
              <Link to="/menu" onClick={() => window.scrollTo(0, 0)}>Menu Complet</Link>
              <Link to="/reserve" onClick={() => window.scrollTo(0, 0)}>Faire une Réservation</Link>
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>Nous Contacter</Link>
              <Link to="/account" onClick={() => window.scrollTo(0, 0)}>Compte Client</Link>
              <Link to="/privacy" onClick={() => window.scrollTo(0, 0)}>Politique de Confidentialité</Link>
              <Link to="/terms" onClick={() => window.scrollTo(0, 0)}>Conditions Générales</Link>
              {/* Removed Access Staff per request */}
            </div>
          </div>

          <div className="footer-section">
            <h4>Horaires</h4>

            <p><strong>Tous les jours</strong><br />11h30 - Minuit</p>
          </div>

          <div className="footer-section">
            <h4>Contact</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { loc: 'Hassan', tel: '05 37 20 65 96' },
                { loc: 'Agdal', tel: '05 37 77 77 78' },
                { loc: 'Hay Riad', tel: '05 37 56 39 06' },
                { loc: 'Salé', tel: '05 37 80 11 00' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 8 }}>
                  <span style={{ color: 'var(--muted)', fontSize: '13px' }}>{item.loc}</span>
                  <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: '13px' }}>{item.tel}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <IconEmail style={{ color: 'var(--accent)' }} />
                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>restocoq.nk@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          &copy; {new Date().getFullYear()} Coq Magic. Tous droits réservés. Designed for excellence.
        </div>
      </footer>
    </>
  )
}


function Header({ cartCount, user, profile, onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  };

  const openMobileMenu = () => {
    setMobileMenuOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleLinkClick = () => {
    closeMobileMenu();
    window.scrollTo(0, 0);
  };

  const handleLogoutClick = () => {
    closeMobileMenu();
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    if (onLogout) {
      await onLogout();
    }
    navigate('/');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <>
      <header className="header">
        <div className="nav">
          <Link to="/" className="brand" onClick={handleLinkClick}>
            {/* <Logo /> removed for cleaner look */}
            <div style={{ textDecoration: 'none', display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ color: 'var(--text)', fontWeight: 900, letterSpacing: '-0.3px' }}>Coq</span>
              <span style={{ color: 'var(--accent)', fontWeight: 900, letterSpacing: '-0.3px' }}>Magic</span>
            </div>
          </Link>
          <nav className="nav-links">
            <Link to="/" onClick={handleLinkClick}>Accueil</Link>
            <Link to="/menu" onClick={handleLinkClick}>Menu</Link>
            <Link to="/reserve" onClick={handleLinkClick}>Réserver</Link>
            <Link to="/contact" onClick={handleLinkClick}>Contact</Link>
            {user ? (
              <Link to="/account" onClick={handleLinkClick}>Mon Compte</Link>
            ) : (
              <Link to="/login" onClick={handleLinkClick} onMouseEnter={() => import('./LoginPage')}>Connexion</Link>
            )}
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* User auth button for desktop */}
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="desktop-only">
                {profile?.role && ['admin', 'manager', 'equipier'].includes(profile.role) && (
                  <Link to="/admin" className="btn" style={{ padding: '8px 12px', fontSize: 12 }} onClick={handleLinkClick}>
                    Dashboard
                  </Link>
                )}
                <button
                  onClick={handleLogoutClick}
                  className="btn"
                  style={{ padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Déconnexion
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn primary desktop-only" style={{ padding: '10px 16px', fontSize: 13, textDecoration: 'none' }} onClick={handleLinkClick}>
                Connexion
              </Link>
            )}
            <Link to="/cart" className="cart-nav-btn" style={{ position: 'relative' }} onClick={handleLinkClick}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {cartCount > 0 && (
                <span style={{ position: 'absolute', top: '-10px', right: '-8px', background: 'var(--accent)', color: '#ffffff', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '11px', boxShadow: '0 3px 10px rgba(220,20,20,0.4)', border: '2px solid var(--bg)', animation: 'pulse 2s infinite' }}>
                  {cartCount}
                </span>
              )}
            </Link>
            <button className="mobile-menu-toggle" onClick={openMobileMenu} aria-label="Open menu">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`} onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeMobileMenu();
          }
        }}>
          <button className="mobile-menu-close" onClick={closeMobileMenu} aria-label="Close menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div className="mobile-menu-links" onClick={(e) => e.stopPropagation()}>
            <Link to="/" onClick={handleLinkClick}>Accueil</Link>
            <Link to="/menu" onClick={handleLinkClick}>Menu</Link>
            <Link to="/reserve" onClick={handleLinkClick}>Réserver</Link>
            <Link to="/contact" onClick={handleLinkClick}>Contact</Link>
            {user ? (
              <>
                <Link to="/account" onClick={handleLinkClick}>Mon Compte</Link>
                {profile?.role && ['admin', 'manager', 'equipier'].includes(profile.role) && (
                  <Link to="/admin" onClick={handleLinkClick}>Dashboard</Link>
                )}
                <button onClick={handleLogoutClick} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 18, fontWeight: 600, cursor: 'pointer', padding: '12px 0', textAlign: 'left' }}>
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/login" onClick={handleLinkClick}>Connexion</Link>
            )}
          </div>
        </div>
      </header >

      {/* Logout Confirmation Modal */}
      {
        showLogoutModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 20,
            backdropFilter: 'blur(4px)'
          }} onClick={cancelLogout}>
            <div style={{
              background: 'var(--card)',
              borderRadius: 16,
              padding: '32px 28px',
              maxWidth: 360,
              width: '100%',
              textAlign: 'center',
              border: '1px solid var(--border)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                width: 56,
                height: 56,
                background: 'rgba(239,68,68,0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                border: '2px solid rgba(239,68,68,0.2)'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </div>
              <h3 style={{ color: 'var(--text)', margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>
                Se déconnecter?
              </h3>
              <p style={{ color: 'var(--muted)', margin: '0 0 24px', fontSize: 14, lineHeight: 1.5 }}>
                Vous allez être déconnecté de votre compte Coq Magic.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  onClick={confirmLogout}
                  className="btn"
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    fontSize: 15,
                    fontWeight: 600,
                    background: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8
                  }}
                >
                  Déconnexion
                </button>
                <button
                  onClick={cancelLogout}
                  className="btn"
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    fontSize: 15,
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--muted)'
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  )
}

function Hero() {
  // Video background for hero section
  const heroVideo = '/assets/videos/AQONWYGfRoaiRLKFjqXZ3H_4OKruJqjPY1Tx0qK2_YTrXxb-TByzb-6QpCTV6d3hUG_eb0iSYdTZ2zs9-If0JURmirCoM8Vlt6vfd7s.mp4';
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const onCanPlay = () => setVideoReady(true);
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('playing', onCanPlay);

    const tryPlay = async () => {
      try {
        const p = el.play();
        if (p && typeof p.then === 'function') await p;
      } catch {

      }
    };
    tryPlay();

    return () => {
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('playing', onCanPlay);
    };
  }, []);

  return (
    <section className="hero" style={{ position: 'relative' }}>
      <video
        ref={videoRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', filter: 'blur(3px)', opacity: videoReady ? 1 : 0, transition: 'opacity 220ms ease-out' }}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="https://images.unsplash.com/photo-1544025162-d7669d2a6c87?q=80&w=1600&auto=format&fit=crop"
      >
        <source src={heroVideo} type="video/mp4" />
      </video>
      <div className="hero-overlay" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.4) 60%, transparent 100%)' }} />
      <div className="hero-inner">
        <div className="left content">
          <small style={{ color: 'var(--accent)', fontWeight: 900, fontSize: '12px', letterSpacing: '3px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8, marginBottom: '16px', fontFamily: "'Unbounded',sans-serif" }}><IconFire /> Grillades au Feu de Bois</small>
          <h1 className="hero-title"><span>Coq</span> <span style={{ color: 'var(--accent)' }}>Magic</span></h1>
          <p className="hero-desc">Restaurant spécialisé dans les grillades au feu de bois. Poulets, Coquelets, Méchouis rôtis lentement pour une saveur incomparable. Une expérience culinaire authentique qui ravit vos papilles.</p>
          <div className="hero-buttons" style={{ marginTop: 28, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <Link to="/menu" onClick={() => window.scrollTo(0, 0)} className="btn primary">Voir le Menu</Link>
            <Link to="/reserve" onClick={() => window.scrollTo(0, 0)} className="btn">Réserver une Table</Link>
          </div>

          {/* Free Livraison Badge */}
          <div className="free-livraison-badge" style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', background: 'linear-gradient(135deg,rgba(220,20,20,0.15),rgba(220,20,20,0.08))', borderRadius: '12px', border: '2px solid rgba(220,20,20,0.3)', width: 'fit-content' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--accent)' }}>Livraison GRATUITE</div>
          </div>
        </div>
        <aside className="hero-card" style={{ background: 'linear-gradient(135deg,rgba(26,26,26,0.95),rgba(31,31,31,0.9))', backdropFilter: 'blur(10px)', border: '2px solid rgba(220,20,20,0.3)', boxShadow: '0 20px 60px rgba(220,20,20,0.15)' }}>
          <h4 style={{ color: 'var(--accent)' }}>Nos Spécialités</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
            <div style={{ paddingBottom: '16px', borderBottom: '1px solid rgba(220,20,20,0.2)' }}>
              <div style={{ color: 'var(--accent)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', fontWeight: 900 }}>Poulets Rôtis</div>
              <strong style={{ color: 'var(--text)', fontSize: '16px', fontWeight: 800 }}>Entiers ou demis</strong>
            </div>
            <div style={{ paddingBottom: '16px', borderBottom: '1px solid rgba(220,20,20,0.2)' }}>
              <div style={{ color: 'var(--accent)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', fontWeight: 900 }}>Coquelets</div>
              <strong style={{ color: 'var(--text)', fontSize: '16px', fontWeight: 800 }}>Jeunes poulets fermiers</strong>
            </div>
            <div>
              <div style={{ color: 'var(--accent)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', fontWeight: 900 }}>Méchouis</div>
              <strong style={{ color: 'var(--text)', fontSize: '16px', fontWeight: 800 }}>Agneau, chèvre</strong>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(135deg,rgba(220,20,20,0.35),rgba(230,57,70,0.25))', padding: '20px 24px', borderRadius: '12px', textAlign: 'center', border: '2px solid var(--accent)', marginTop: 24, boxShadow: '0 8px 25px rgba(220,20,20,0.25)', backdropFilter: 'blur(10px)' }}>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px', fontWeight: 900 }}>Ouvert Aujourd'hui</div>
            <div style={{ fontWeight: 900, color: '#ffffff', fontSize: '24px', textShadow: '0 2px 10px rgba(220,20,20,0.5)' }}>11:30 - Minuit</div>
          </div>
        </aside>
      </div>
    </section>
  )
}

function Home() {
  const reviews = [
    { author: 'Fatima Al-Omrani', role: 'Rabat', text: '"Les grillades au feu de bois sont exceptionnelles. Le poulet rôti est succulent et savoureux!"' },
    { author: 'Mohammed Al-Kharazi', role: 'Salé', text: '"Cuisson traditionnelle au feu de bois incomparable. Un véritable joyau culinaire marocain."' },
    { author: 'Amina & Hassan', role: 'Rabat', text: '"Méchoui délicieux pour notre mariage. Merci beaucoup Coq Magic pour cette expérience!"' },
    { author: 'Yousuf Al-Bahri', role: 'Hay Riad', text: '"Tradition marocaine et excellence culinaire. À recommander absolument à tous!"' },
    { author: 'Zahra Bennani', role: 'Agdal', text: '"Ambiance chaleureuse, service attentif, et cuisine incomparable. Je reviendrai!"' },
    { author: 'Karim El-Idrissi', role: 'Salé', text: '"La spécialité du méchoui est absolument délicieuse. Meilleur de la région!"' },
    { author: 'Leila Mansouri', role: 'Rabat', text: '"Personnel très professionnel et les plats sont savoureux! Expérience gastronomique."' },
    { author: 'Hassan Benjelloun', role: 'Rabat', text: '"Service impeccable, grillades cuites à la perfection. Bravo à toute l\'équipe!"' },
    { author: 'Nadia El-Bachir', role: 'Salé', text: '"Portions généreuses et le prix est très juste. Je reviendrai certainement!"' },
    { author: 'Rachid Ben Ali', role: 'Hay Riad', text: '"Meilleure adresse pour les grillades au feu de bois. Je recommande vivement!"' },
    { author: 'Souad Lahlou', role: 'Agdal', text: '"Atmosphère conviviale et nourriture délicieuse. Parfait pour toute la famille!"' },
    { author: 'Mohamed Tahir', role: 'Rabat', text: '"Qualité exceptionnelle. Les grillades sont toujours fraîches et savoureuses!"' }
  ];

  const mid = Math.ceil(reviews.length / 2);
  const firstRow = reviews.slice(0, mid);
  const secondRow = reviews.slice(mid);

  const ReviewCard = ({ author, role, text }) => {
    return (
      <figure className="review-card">
        <div className="testimonial-stars">★★★★★</div>
        <blockquote className="review-text">{text}</blockquote>
        <div className="review-meta">
          <div className="testimonial-author">{author}</div>
          <div className="testimonial-role">{role}</div>
        </div>
      </figure>
    );
  };

  return (
    <main style={{ flex: 1 }}>
      <Hero />

      {/* Chef Recommendations */}
      <section className="container" data-reveal>
        <div className="section-title">
          <div>
            <h2><span>Les</span> <span style={{ color: 'var(--accent)' }}>Recommandations</span></h2>
            <div className="section-subtitle">Nos plats signature rôtis au feu de bois</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/menu" className="btn">Voir le Menu Complet →</Link>
          </div>
        </div>
        <MenuPreview />
      </section>

      {/* Testimonials with 2-Row Carousel */}
      <section className="container" data-reveal>
        <div className="section-title">
          <div>
            <h2><span>Avis de</span> <span style={{ color: 'var(--accent)' }}>Nos Clients</span></h2>
            <div className="section-subtitle">Expériences inoubliables chez Coq Magic</div>
          </div>
        </div>

        <div className="testimonials-marquee" style={{ marginTop: 48 }}>
          <div className="marquee-row">
            <div className="marquee-track">
              {[...firstRow, ...firstRow].map((t, i) => (
                <ReviewCard key={`r1-${i}`} author={t.author} role={t.role} text={t.text} />
              ))}
            </div>
          </div>
          <div className="marquee-row reverse">
            <div className="marquee-track">
              {[...secondRow, ...secondRow].map((t, i) => (
                <ReviewCard key={`r2-${i}`} author={t.author} role={t.role} text={t.text} />
              ))}
            </div>
          </div>
          <div className="marquee-fade left" />
          <div className="marquee-fade right" />
        </div>
      </section>

      {/* Locations - Horizontal Scroll */}
      <section className="container" data-reveal>
        <div className="section-title">
          <div>
            <h2><span>Nos</span> <span style={{ color: 'var(--accent)' }}>Localisations</span></h2>
            <div className="section-subtitle">Faites glisser pour découvrir nos adresses</div>
          </div>
        </div>
        <div className="locations-scroll-container" style={{ display: 'flex', overflowX: 'auto', gap: 20, padding: '16px 0', WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none', marginTop: 32 }}>
          {[{ name: 'Hassan', addr: 'Rabat', phone: '0537206596', query: 'Coq Magic Hassan Rabat Morocco', id: 'hassan' },
          { name: 'Agdal', addr: 'Rabat', phone: '0537777778', query: 'Coq Magic Agdal Rabat Morocco', id: 'agdal' },
          { name: 'Hay Riad', addr: 'Rabat', phone: '0537563906', query: 'Coq Magic Hay Riad Rabat Morocco', id: 'hayriad' },
          { name: 'Salé', addr: 'Hay Salam', phone: '0537801100', query: 'Coq Magic Hay Salam Sale Morocco', id: 'sale' }
          ].map(loc => (
            <div key={loc.id} className="location-card" style={{ flex: '0 0 300px', scrollSnapAlign: 'start', background: 'var(--card)', border: '2px solid rgba(220,20,20,0.2)', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(220,20,20,0.12)' }}>
              <iframe width="100%" height="160" frameBorder="0" scrolling="no" marginHeight="0" marginWidth="0" src={`https://maps.google.com/maps?q=${encodeURIComponent(loc.query)}&hl=fr&z=16&output=embed`} style={{ border: 'none' }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`Map of ${loc.name}`}></iframe>
              <div className="location-info" style={{ padding: '18px' }}>
                <h3 style={{ color: 'var(--accent)', fontSize: '20px', fontWeight: 900, marginBottom: 6, marginTop: 0 }}>{loc.name}</h3>
                <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: 12, lineHeight: 1.4 }}>{loc.addr}</p>
                <a href={`tel:${loc.phone}`} className="location-phone" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent)', fontSize: '15px', fontWeight: 700, textDecoration: 'none' }}>
                  <IconPhone />
                  <span>{loc.phone.replace(/(\d{4})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3-$4')}</span>
                </a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 12, color: 'var(--muted)', fontSize: '13px' }}>
          ← Faites glisser pour voir les autres adresses →
        </div>
      </section>
    </main>
  )
}

function MenuPreview() {
  // Initial state is empty to avoid ghost data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        setLoading(true);
        // Import dynamically to avoid circular dependency
        const { getMenuItems } = await import('./supabaseClient');
        const result = await getMenuItems();
        if (IS_DEV) console.log("MenuPage: Fetched items from DB:", result);
        if (result.success && result.items.length > 0) {
          const allItems = result.items || [];

          // Filter featured items, or take first 6 if none are featured
          const featured = allItems.filter(i => i.is_featured);
          if (featured.length > 0) {
            setItems(featured.slice(0, 8).map(dbItem => ({
              id: dbItem.id,
              title: dbItem.title,
              desc: dbItem.description || '',
              price: dbItem.price,
              img: dbItem.image_url || '/assets/menu/default.jpg',
              cat: dbItem.category
            })));
          } else if (allItems.length > 0) {
            // No featured items, show first 8 from database
            setItems(allItems.slice(0, 8).map(dbItem => ({
              id: dbItem.id,
              title: dbItem.title,
              desc: dbItem.description || '',
              price: dbItem.price,
              img: dbItem.image_url || '/assets/menu/default.jpg',
              cat: dbItem.category
            })));
          }
        }
      } catch (err) {
        if (IS_DEV) console.log('Error loading featured menu:', err);
      } finally {
        setLoading(false);
      }
    };
    loadFeatured();
  }, []);

  if (loading) {
    return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px', marginTop: '60px' }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ height: '400px', background: 'var(--card)', borderRadius: '16px', animation: 'pulse 1.5s infinite' }}></div>
      ))}
    </div>;
  }

  return (
    <div className="menu-grid recommendations" style={{ marginTop: 18 }}>
      {items.map(it => <ProductCard key={it.id} item={it} />)}
    </div>
  )
}

function ProductCard({ item }) {
  const navigate = useNavigate();
  return (
    <div className="card" style={{ cursor: 'pointer' }}>
      <div className="thumb" onClick={() => navigate('/product/' + item.id)} style={{ cursor: 'pointer' }}>
        <img src={item.img} alt={item.title} loading="lazy" />
      </div>
      <h3 onClick={() => navigate('/product/' + item.id)} style={{ cursor: 'pointer' }}>{item.title}</h3>
      <div className="card-desc">{item.desc}</div>
      <div className="price">{item.price} DH</div>
      <div className="actions">
        <button className="btn" onClick={() => navigate('/product/' + item.id)} style={{ flex: 1 }}>Détails</button>
        <AddToCartButton item={item} small />
      </div>
    </div>
  )
}

function AddToCartButton({ item, small }) {
  const { addToCart } = useOrder();
  const [isAdding, setIsAdding] = useState(false);

  const add = (e) => {
    if (e) e.stopPropagation();
    if (isAdding) return;
    setIsAdding(true);
    addToCart({ ...item, qty: 1 });
    setTimeout(() => setIsAdding(false), 1500);
  }

  return (
    <button className={small ? 'btn' : 'btn primary'} onClick={add} disabled={isAdding} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: isAdding ? 0.6 : 1 }}>
      <IconCart />
      {isAdding ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><IconCheck /> Ajouté</span> : (small ? '+' : <><IconCart /> Ajouter au Panier</>)}
    </button>
  )
}

function MenuPage() {
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('all');
  // Initial state empty to avoid ghost data
  const [items, setItems] = useState([]);

  useEffect(() => {
    const loadMenu = async () => {
      try {
        const { getMenuItems } = await import('./supabaseClient');
        const result = await getMenuItems();
        if (IS_DEV) console.log("MenuPage: Fetched items from DB:", result);
        if (result.success && result.items.length > 0) {
          const allItems = result.items || [];
          // Sort by sort_order
          allItems.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

          setItems(allItems.map(dbItem => ({
            id: dbItem.id,
            title: dbItem.title,
            desc: dbItem.description || '',
            price: dbItem.price,
            img: dbItem.image_url || '/assets/menu/default.jpg',
            cat: dbItem.category,
            is_featured: dbItem.is_featured
          })));
        }
        // If DB is empty, we're already showing SAMPLE_MENU
      } catch (err) {
        if (IS_DEV) console.log('Error loading menu:', err);
        // Already showing sample data
      }
    };
    loadMenu();
  }, []);

  const normalizeCategory = (value) => {
    if (!value) return '';
    const v = String(value)
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ');

    const legacyToReal = {
      poulets: 'poulet',
      coquelets: 'poulet',
      grillades: 'grillade',
      salades: 'salade'
    };
    if (legacyToReal[v]) return legacyToReal[v];

    if (v.endsWith('s')) {
      const singular = v.slice(0, -1);
      if (legacyToReal[singular]) return legacyToReal[singular];
      return singular;
    }
    return v;
  };

  const filteredItems = useMemo(() => items.filter(i => {
    const itemCat = normalizeCategory(i.cat);
    const filterCat = normalizeCategory(cat);

    const catMatch = filterCat === 'all' || itemCat === filterCat;

    const q = query.trim().toLowerCase();
    const searchMatch = !q ||
      i.title.toLowerCase().includes(q) ||
      i.desc.toLowerCase().includes(q);

    return catMatch && searchMatch;
  }).slice().sort((a, b) => {
    const weight = (it) => {
      if (it?.is_featured) return 0;
      const c = normalizeCategory(it?.cat);
      const w = {
        menu: 1,
        poulet: 2,
        grillade: 3,
        accompagnement: 4,
        salade: 5,
        jus: 6,
        boisson: 7,
        boissons: 7,
        dessert: 8
      };
      return w[c] ?? 50;
    };

    const wa = weight(a);
    const wb = weight(b);
    if (wa !== wb) return wa - wb;

    const pa = Number(a?.price ?? 0);
    const pb = Number(b?.price ?? 0);
    if (pa !== pb) return pb - pa;

    return String(a?.title || '').localeCompare(String(b?.title || ''), 'fr');
  }), [query, cat, items]);

  const categories = MENU_CATEGORIES;

  return (
    <div className="menu-page-container" style={{ paddingTop: 24 }}>
      <div className="container">
        <div className="section-title">
          <div>
            <h2><span>Notre</span> <span style={{ color: 'var(--accent)' }}>Menu</span></h2>
            <div className="section-subtitle">Découvrez notre sélection de grillades au feu de bois</div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-container-mobile" style={{ background: 'linear-gradient(135deg,rgba(220,20,20,0.08),rgba(220,20,20,0.04))', border: '2px solid rgba(220,20,20,0.15)', borderRadius: '18px', padding: '32px', marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--card)', border: '2px solid var(--border)', borderRadius: '14px', padding: '14px 18px', transition: 'all 0.3s' }}>
            <IconSearch />
            <input
              placeholder="Rechercher un plat..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', fontSize: '15px', fontWeight: 500 }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', transition: 'all 0.3s' }}
              >
                <IconClose />
              </button>
            )}
          </div>
        </div>

        {/* Filter Categories */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '16px' }}>
            <IconFilter />
            <label style={{ color: 'var(--text)', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>Filtrer par Catégorie</label>
          </div>
          <div className="category-filters-mobile" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {categories.map(c => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                style={{
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: '2px solid',
                  background: cat === c.id ? 'linear-gradient(135deg,var(--accent),var(--accent2))' : 'var(--card)',
                  color: cat === c.id ? '#ffffff' : 'var(--text)',
                  borderColor: cat === c.id ? 'transparent' : 'var(--border)',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: cat === c.id ? '0 4px 15px rgba(220,20,20,0.3)' : 'none',
                  textTransform: 'capitalize',
                  minHeight: '44px'
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results Info */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', padding: '0 8px' }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '15px' }}>
            <span style={{ background: 'linear-gradient(135deg,var(--accent),var(--accent2))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 900 }}>{filteredItems.length}</span>
            <span style={{ color: 'var(--muted)', marginLeft: '8px' }}>{filteredItems.length === 1 ? 'plat' : 'plats'} trouvé(s)</span>
          </div>
          {(query || cat !== 'all') && (
            <button
              onClick={() => { setQuery(''); setCat('all') }}
              style={{ background: 'transparent', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 700, padding: '6px 12px', borderRadius: '8px', transition: 'all 0.3s', fontSize: '14px' }}
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>

        {/* Menu Grid */}
        <div className="menu-grid">
          {filteredItems.map(it => <ProductCard key={it.id} item={it} />)}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--muted)' }}>
            <div style={{ marginBottom: '16px', color: 'var(--muted)' }}><IconPlate /></div>
            <h3 style={{ color: 'var(--text)', marginBottom: '8px', fontSize: '24px' }}>Aucun plat trouvé</h3>
            <p style={{ marginBottom: '24px', fontSize: '16px' }}>Essayez d\'ajuster vos filtres de recherche</p>
            <button onClick={() => { setQuery(''); setCat('all') }} className="btn primary">Réinitialiser les filtres</button>
          </div>
        )}
      </div>
    </div>
  )
}

function ProductPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Try to load from Supabase first, fallback to SAMPLE_MENU
    const loadProduct = async () => {
      setLoading(true);
      try {
        const { getMenuItems } = await import('./supabaseClient');
        const result = await getMenuItems();
        if (result.success && result.items?.length > 0) {
          const found = result.items.find(x => String(x.id) === id);
          if (found) {
            setItem({
              id: found.id,
              title: found.title,
              desc: found.description || '',
              price: found.price,
              img: found.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=900',
              category: found.category
            });
            setLoading(false);
            return;
          }
        }
      } catch (e) {

      }
      // Fallback to SAMPLE_MENU
      const sample = SAMPLE_MENU.find(x => String(x.id) === id);
      setItem(sample || { title: 'Non trouvé', desc: '', price: 0, img: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=900' });
      setLoading(false);
    };
    loadProduct();
  }, [id]);

  const { addToCart } = useOrder();
  const [qty, setQty] = useState(1);
  const [showModal, setShowModal] = useState(false);

  const add = () => {
    addToCart({ ...item, qty });
    setShowModal(true);
    setTimeout(() => setShowModal(false), 2500);
  }

  return (
    <div className="container" style={{ paddingTop: 40 }}>
      {loading || !item ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Chargement...</div>
      ) : (
        <>
          <Link to="/menu" style={{ color: 'var(--accent)', textDecoration: 'none', marginBottom: '24px', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all 0.3s', padding: '8px 12px', borderRadius: '8px' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(220,20,20,0.1)'; e.currentTarget.style.transform = 'translateX(-4px)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            Retour au Menu
          </Link>
          <div className="product-page">
            <div className="product-hero"><img src={item.img} alt={item.title} style={{ width: '100%', height: 'auto', display: 'block' }} /></div>
            <div>
              <small style={{ color: 'var(--accent)', fontWeight: 800, fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}>Spécialité</small>
              <h2 style={{ color: 'var(--text)', marginTop: '8px', marginBottom: '16px', fontSize: '32px' }}>{item.title}</h2>
              <p style={{ color: 'var(--muted)', fontSize: '16px', lineHeight: 1.6, marginBottom: '24px' }}>{item.desc}</p>

              <div style={{ background: 'var(--luxury-dark)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '24px', transition: 'all 0.3s' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(220,20,20,0.4)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(220,20,20,0.15)' }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase' }}>Prix unitaire</div>
                <div style={{ fontWeight: 900, color: 'var(--accent)', fontSize: '36px' }}>{item.price} DH</div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: 'var(--text)', fontWeight: 700, marginBottom: '8px' }}>Quantité</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button className="btn" onClick={() => setQty(Math.max(1, qty - 1))} style={{ width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 900 }}>−</button>
                  <input type="number" value={qty} min={1} max={99} onChange={e => setQty(Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)))} style={{ width: '100px', padding: '12px', borderRadius: '10px', border: '2px solid var(--border)', background: 'var(--luxury-dark)', color: 'var(--text)', textAlign: 'center', fontWeight: 700, fontSize: '16px' }} />
                  <button className="btn" onClick={() => setQty(Math.min(99, qty + 1))} style={{ width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 900 }}>+</button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button className="btn primary" onClick={add} style={{ padding: '16px', fontSize: '16px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%' }}>
                  <IconCart />
                  Ajouter au Panier
                </button>
                <Link to="/cart" className="btn" style={{ padding: '16px', fontSize: '16px', fontWeight: 800, textAlign: 'center', textDecoration: 'none', width: '100%' }}>Voir le Panier</Link>
                <Link to="/menu" className="btn" style={{ padding: '16px', fontSize: '16px', fontWeight: 800, textAlign: 'center', textDecoration: 'none', width: '100%' }}>Retour au Menu</Link>
              </div>
            </div>
          </div>
          <Modal
            isOpen={showModal}
            title={<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><IconCheck /> Ajouté au Panier</span>}
            message={`${qty} × ${item.title} a été ajouté à votre panier.`}
            onClose={() => setShowModal(false)}
          />
        </>
      )}
    </div>
  )
}


function CartPage() {
  const { cart, updateQuantity, removeFromCart, subtotal, total } = useOrder();

  // Use OrderContext totals for consistency with Checkout
  const tax = Math.round(subtotal * 0.1);

  const inc = (id) => {
    const item = cart.find(i => i.id === id);
    if (item) updateQuantity(id, item.qty + 1);
  };

  const dec = (id) => {
    const item = cart.find(i => i.id === id);
    if (item) updateQuantity(id, item.qty - 1);
  };

  return (
    <div className="container" data-reveal style={{ paddingTop: 40, minHeight: '60vh' }}>
      <div className="section-title">
        <div>
          <h2><span>Votre</span> <span style={{ color: 'var(--accent)' }}>Panier</span></h2>
          <div style={{ color: 'var(--muted)' }}>Vérifiez vos articles avant de commander</div>
        </div>
      </div>

      <div className="cart-page-grid">
        <div className="cart-items">
          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)', background: 'var(--card)', borderRadius: '16px', border: '1px solid var(--border)' }}>
              <div style={{ marginBottom: 16, opacity: 0.5 }}><IconCartLarge /></div>
              <p>Votre panier est vide</p>
              <Link to="/menu" className="btn primary" style={{ marginTop: 16, display: 'inline-block', textDecoration: 'none' }}>Découvrir le Menu</Link>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {cart.map(ci => (
                <div key={ci.id} className="card-item-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'var(--card)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <div>
                    <strong style={{ color: 'var(--text)', fontSize: '16px', display: 'block', marginBottom: '6px' }}>{ci.title}</strong>
                    <div style={{ color: 'var(--muted)', fontSize: '14px' }}>{ci.price} DH × <strong>{ci.qty}</strong> = <strong style={{ color: 'var(--accent)' }}>{(ci.price * ci.qty).toLocaleString()}</strong> DH</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn" onClick={() => dec(ci.id)} style={{ width: '40px', height: '40px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 900 }}>−</button>
                    <button className="btn" onClick={() => inc(ci.id)} style={{ width: '40px', height: '40px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 900 }}>+</button>
                    <button className="btn" onClick={() => removeFromCart(ci.id)} style={{ width: '40px', height: '40px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(220,100,100,0.1)', color: '#d64545', borderColor: 'rgba(220,100,100,0.2)', fontSize: '18px' }}><IconClose /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cart-summary">
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 24px', color: 'var(--text)' }}>Résumé de la Commande</h3>

            {cart.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--muted)' }}>Sous-total</span>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{subtotal.toLocaleString()} DH</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--muted)' }}>Taxes (10%)</span>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{tax.toLocaleString()} DH</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', fontSize: '18px' }}>
                  <span style={{ color: 'var(--accent)', fontWeight: 900 }}>Total</span>
                  <span style={{ color: 'var(--accent)', fontWeight: 900 }}>{total.toLocaleString()} DH</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Link to="/checkout" className="btn primary" style={{ width: '100%', padding: '14px', textAlign: 'center', textDecoration: 'none' }}>Procéder au Paiement</Link>
                  <Link to="/menu" className="btn" style={{ width: '100%', padding: '14px', textAlign: 'center', textDecoration: 'none' }}>Continuer les Achats</Link>
                </div>
              </>
            )}

            {cart.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '14px' }}>
                Ajoutez des articles pour voir le résumé
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ReservePage() {
  const [reservations, setReservations] = useState([]); // Session-only history
  const [form, setForm] = useState({ name: '', phone: '', date: '', time: '', guests: '2 Personnes', notes: '' });
  const [lastReservation, setLastReservation] = useState(null); // Store submitted values for success modal
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      alert('Veuillez remplir tous les champs obligatoires');
      return
    }

    setIsSubmitting(true);
    try {
      const { createReservation } = await import('./supabaseClient');
      const result = await createReservation(form);

      if (result.success) {
        const savedReservation = { ...form, id: result.reservation?.id || Date.now(), created: new Date().toISOString() };
        setReservations([...reservations, savedReservation]);
        setLastReservation({ date: form.date, time: form.time, guests: form.guests }); // Store for modal before reset
        setForm({ name: '', phone: '', date: '', time: '', guests: '2 Personnes', notes: '' });
        setShowModal(true);
      } else {
        alert('Erreur: ' + (result.error || 'Impossible de créer la réservation'));
      }
    } catch (err) {
      console.error(err);
      alert('Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  }
  return (
    <div className="container">
      <div className="section-title"><div><h2><span>Réserver une</span> <span style={{ color: 'var(--accent)' }}>Table</span></h2><div style={{ color: 'var(--muted)' }}>Réservez votre expérience culinaire parfaite</div></div></div>
      <form className="form reserve-form-container" onSubmit={submit} style={{ maxWidth: 640, marginTop: 12 }}>
        <input placeholder="Nom complet" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        <input placeholder="Téléphone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required />
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
          <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} required />
        </div>
        <select value={form.guests} onChange={e => setForm({ ...form, guests: e.target.value })}>
          <option value="1 Personne">1 Personne</option>
          <option value="2 Personnes">2 Personnes</option>
          <option value="3 Personnes">3 Personnes</option>
          <option value="4 Personnes">4 Personnes</option>
          <option value="5 Personnes">5 Personnes</option>
          <option value="6+ Personnes">6+ Personnes</option>
        </select>
        <textarea placeholder="Demandes spéciales ou notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}></textarea>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Confirmation...' : 'Confirmer la Réservation'}
          </button>
        </div>
      </form>

      {reservations.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ color: 'var(--text)' }}>Vos Réservations Récentes</h3>
          {reservations.slice().reverse().map(r => (
            <div key={r.id} className="card" style={{ marginTop: 8 }}>
              <strong style={{ color: 'var(--text)' }}>{r.name}</strong> — {r.date} à {r.time} ({r.guests} {r.guests === '1' ? 'Personne' : 'Personnes'})
              <div style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}><IconPhone /> {r.phone} • {r.notes}</div>
              <div style={{ color: 'var(--accent)', fontSize: '12px', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}><IconCheck /> Demande envoyée</div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        title={<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><IconCheck /> Réservation Confirmée</span>}
        message={lastReservation ? `Votre table pour ${lastReservation.guests} a été réservée pour ${lastReservation.date} à ${lastReservation.time}. Nous avons hâte de vous accueillir!` : 'Votre réservation a été envoyée. Nous avons hâte de vous accueillir!'}
        onClose={() => { setShowModal(false); setLastReservation(null); }}
      />
    </div>
  )
}

function ContactPage() {
  const [messages, setMessages] = useState([]); // Session-only history
  const [form, setForm] = useState({ name: '', contact: '', msg: '', website: '' });
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const submit = async e => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/contact' } });
      return;
    }
    if (!form.name || !form.contact || !form.msg) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    if (form.website) {
      return;
    }

    const name = String(form.name).trim();
    const contact = String(form.contact).trim();
    const msg = String(form.msg).trim();
    if (name.length > 80 || contact.length > 120 || msg.length > 1200) {
      alert('Message trop long');
      return;
    }

    const last = Number(localStorage.getItem('coq_last_contact_submit') || '0');
    if (Date.now() - last < 30000) {
      alert('Veuillez patienter avant d\'envoyer un autre message');
      return;
    }

    setIsSubmitting(true);
    try {
      let result;
      const safeForm = { name, contact, msg };
      if (executeRecaptcha && isSupabaseConfigured()) {
        const token = await executeRecaptcha('contact');
        result = await submitContactWithRecaptcha(token, safeForm);
      } else {
        const { createContactMessage } = await import('./supabaseClient');
        result = await createContactMessage(safeForm);
      }

      if (result.success) {
        localStorage.setItem('coq_last_contact_submit', String(Date.now()));
        setMessages([...messages, { ...safeForm, id: result.message?.id || Date.now(), created: new Date().toISOString() }]);
        setShowModal(true);
        setForm({ name: '', contact: '', msg: '', website: '' });
      } else {
        alert('Erreur: ' + (result.error || 'Impossible d\'envoyer le message'));
      }
    } catch (err) {
      console.error(err);
      alert('Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  }
  return (
    <div className="container">
      <div className="section-title"><div><h2><span>Nous</span> <span style={{ color: 'var(--accent)' }}>Contacter</span></h2><div style={{ color: 'var(--muted)' }}>Nous aimerions entendre parler de vous</div></div></div>
      {!isAuthenticated ? (
        <div className="card" style={{ maxWidth: 640, margin: '0 auto', padding: 18 }}>
          <div style={{ color: 'var(--text)', fontWeight: 800, marginBottom: 8 }}>Connexion requise</div>
          <div style={{ color: 'var(--muted)', marginBottom: 14 }}>Veuillez vous connecter pour envoyer un message.</div>
          <button className="btn primary" type="button" onClick={() => navigate('/login', { state: { from: '/contact' } })}>
            Se connecter
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="form contact-form-container" style={{ maxWidth: 640, margin: '0 auto' }}>
          <input placeholder="Nom complet" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <input placeholder="Email ou Téléphone" value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} required />
          <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} />
          <textarea placeholder="Votre message" value={form.msg} onChange={e => setForm({ ...form, msg: e.target.value })} rows={5} required></textarea>
          <button className="btn primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Envoi...' : 'Envoyer le Message'}
          </button>
          <p style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '16px', textAlign: 'center', lineHeight: '1.5', opacity: 0.8 }}>
            Ce site est protégé par reCAPTCHA et la
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'underline', margin: '0 4px' }}>Politique de confidentialité</a>
            et les
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'underline', margin: '0 4px' }}>Conditions d'utilisation</a>
            de Google s'appliquent.
          </p>
        </form>
      )}

      {messages.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ color: 'var(--text)' }}>Messages Envoyés (Session)</h3>
          {messages.slice().reverse().map(m => (
            <div key={m.id} className="card" style={{ marginTop: 8 }}><strong style={{ color: 'var(--text)' }}>{m.name}</strong> • {m.contact}<div style={{ color: 'var(--muted)', marginTop: 8 }}>{m.msg}</div><div style={{ color: 'var(--accent)', fontSize: '12px', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}><IconCheck /> Envoyé</div></div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        title={<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><IconCheck /> Message Envoyé</span>}
        message="Merci de nous avoir contactés. Nous vous répondrons dès que possible!"
        onClose={() => setShowModal(false)}
      />
    </div>
  )
}


function UnauthorizedPage() {
  return (
    <div className="container" style={{ paddingTop: 80, textAlign: 'center', minHeight: '60vh' }}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', background: 'rgba(239,68,68,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
          border: '2px solid rgba(239,68,68,0.2)'
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h1 style={{ color: 'var(--text)', fontSize: 32, marginBottom: 16 }}>Accès Refusé</h1>
        <p style={{ color: 'var(--muted)', marginBottom: 32, lineHeight: 1.6 }}>
          Vous n'avez pas les permissions nécessaires pour accéder à cette page.
        </p>
        <Link to="/" className="btn primary" style={{ textDecoration: 'none' }}>
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}


function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const base = TITLE_MAP[pathname] || 'Coq Magic - Restaurant & Livraison';
    document.title = base;
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

function ScrollRevealManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll('[data-reveal]'));
    if (targets.length === 0) return;

    targets.forEach(el => el.classList.add('reveal'));

    if (!('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);
  return null;
}


export default function App() {
  return (
    <PasswordGate>
      <Router>
        <DocumentTitle />
        <ScrollToTop />
        <ScrollRevealManager />
        <AuthProvider>
          <OrderProvider>
            <div className="app">
              <Suspense fallback={
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--bg)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 60, height: 60, border: '4px solid rgba(220,20,20,0.1)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <div style={{ marginTop: 20, fontSize: 18, fontWeight: 700, fontFamily: "'Unbounded', sans-serif" }}>
                    <span style={{ color: 'var(--text)' }}>Coq</span> <span style={{ color: 'var(--accent)' }}>Magic</span>
                  </div>
                </div>
              }>
                <Routes>
                  <Route path="/" element={<><HeaderWrapper /><Home /><Footer /></>} />
                  <Route path="/menu" element={<><HeaderWrapper /><MenuPage /><Footer /></>} />
                  <Route path="/product/:id" element={<><HeaderWrapper /><ProductPage /><Footer /></>} />
                  <Route path="/cart" element={<><HeaderWrapper /><CartPage /><Footer /></>} />
                  <Route path="/checkout" element={<><HeaderWrapper /><CheckoutPage /><Footer /></>} />
                  <Route path="/reserve" element={<><HeaderWrapper /><ReservePage /><Footer /></>} />
                  <Route path="/contact" element={<><HeaderWrapper /><ContactPage /><Footer /></>} />
                  <Route path="/account" element={<><HeaderWrapper /><AccountPage /><Footer /></>} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/admin" element={
                    <ProtectedRoute requiredRole="equipier">
                      <><HeaderWrapper /><AdminDashboard /><Footer /></>
                    </ProtectedRoute>
                  } />
                  <Route path="/unauthorized" element={<><HeaderWrapper /><UnauthorizedPage /><Footer /></>} />
                  <Route path="/privacy" element={<><HeaderWrapper /><PrivacyPage /><Footer /></>} />
                  <Route path="/terms" element={<><HeaderWrapper /><TermsPage /><Footer /></>} />
                  <Route path="*" element={<><HeaderWrapper /><div className="container"><h2>404 - Page Not Found</h2></div><Footer /></>} />
                </Routes>
              </Suspense>
            </div>
          </OrderProvider>
        </AuthProvider>
      </Router>
    </PasswordGate>
  )
}

function HeaderWrapper() {
  const { cart } = useOrder();
  const { user, profile, signOut } = useAuth();
  return <Header cartCount={cart.reduce((s, i) => s + i.qty, 0)} user={user} profile={profile} onLogout={signOut} />;
}

// Service Worker Registration for PWA
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('SW registered: ', registration);
      })
      .catch(registrationError => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}
